/**
 * Conversion + Play Store prompts — local only, no paid APIs.
 *
 * Buy reminder: after 3 completed classes (free users), then every 4 more clears,
 * with 2-day cooldown between prompts.
 *
 * Rate reminder: after 5 clears OR first exam pass, then every 8 clears,
 * with 14-day cooldown. Skipped once user rates / dismisses permanently.
 */

const BUY_KEY = "upt_prompt_buy";
const RATE_KEY = "upt_prompt_rate";
const SESSION_KEY = "upt_session_clears";

export interface PromptState {
  lastShownAt: number;
  timesShown: number;
  dismissedForever: boolean;
}

function read(key: string): PromptState {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { lastShownAt: 0, timesShown: 0, dismissedForever: false };
    return { ...{ lastShownAt: 0, timesShown: 0, dismissedForever: false }, ...JSON.parse(raw) };
  } catch {
    return { lastShownAt: 0, timesShown: 0, dismissedForever: false };
  }
}

function write(key: string, state: PromptState): void {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

export function bumpSessionClears(): number {
  let n = 0;
  try {
    n = Number(localStorage.getItem(SESSION_KEY) || "0") || 0;
  } catch {
    n = 0;
  }
  n += 1;
  try {
    localStorage.setItem(SESSION_KEY, String(n));
  } catch {
    /* ignore */
  }
  return n;
}

export function getSessionClears(): number {
  try {
    return Number(localStorage.getItem(SESSION_KEY) || "0") || 0;
  } catch {
    return 0;
  }
}

const DAY = 86_400_000;

/** Free user: show buy after 3 clears, then every 4, cooldown 2 days. Max 8 soft prompts. */
export function shouldShowBuyPrompt(opts: {
  hasFullCourse: boolean;
  clears: number;
}): boolean {
  if (opts.hasFullCourse) return false;
  if (opts.clears < 3) return false;
  const st = read(BUY_KEY);
  if (st.dismissedForever || st.timesShown >= 8) return false;
  if (Date.now() - st.lastShownAt < 2 * DAY) return false;
  // Show on 3, 7, 11, 15…
  if (opts.clears === 3) return true;
  return (opts.clears - 3) % 4 === 0;
}

export function markBuyPromptShown(): void {
  const st = read(BUY_KEY);
  st.lastShownAt = Date.now();
  st.timesShown += 1;
  write(BUY_KEY, st);
}

export function dismissBuyPromptForever(): void {
  const st = read(BUY_KEY);
  st.dismissedForever = true;
  st.lastShownAt = Date.now();
  write(BUY_KEY, st);
}

/** Rate: after 5 clears, then every 8; 14-day cooldown. */
export function shouldShowRatePrompt(opts: {
  clears: number;
  examPassed?: boolean;
}): boolean {
  if (opts.clears < 5 && !opts.examPassed) return false;
  const st = read(RATE_KEY);
  if (st.dismissedForever || st.timesShown >= 5) return false;
  if (Date.now() - st.lastShownAt < 14 * DAY) return false;
  if (opts.examPassed && st.timesShown === 0) return true;
  if (opts.clears < 5) return false;
  return opts.clears === 5 || (opts.clears - 5) % 8 === 0;
}

export function markRatePromptShown(): void {
  const st = read(RATE_KEY);
  st.lastShownAt = Date.now();
  st.timesShown += 1;
  write(RATE_KEY, st);
}

export function dismissRatePromptForever(): void {
  const st = read(RATE_KEY);
  st.dismissedForever = true;
  st.lastShownAt = Date.now();
  write(RATE_KEY, st);
}

import { PLAY_STORE_URL as DEFAULT_PLAY_STORE_URL } from "./shareCopy";

/** Play Store URL — override via localStorage `uptPlayStoreUrl` if needed. */
export const PLAY_STORE_URL =
  (typeof localStorage !== "undefined" &&
    localStorage.getItem("uptPlayStoreUrl")?.trim()) ||
  DEFAULT_PLAY_STORE_URL;
